'use client';

import {
  forwardRef,
  useState,
  type ComponentPropsWithoutRef,
  type KeyboardEvent,
} from 'react';
import {
  Avatar as MBAvatar,
  AvatarGroup,
  Button,
  Checkbox as MBCheckbox,
  Modal,
  Popover,
} from '@mokoboko/components';
import { ChevronDown } from 'lucide-react';

import { useIsMobile } from '@/hooks/use-is-mobile';
import { getAbbreviatedName, getFullName, mergeClassNames } from '@/lib/utils';
import type { Instructor } from '@/state/class-availabilities';

type PickerProps = {
  instructors: Instructor[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
};

const nameOf = (item: Instructor) =>
  getAbbreviatedName(item.first_name, item.last_name);

// No ids is "All". The first instructor picked from it narrows to that one
// alone, so finding one teacher among eight is one tap, not seven. Unticking
// the last pick shown goes back to All through normalizeInstructorSelection,
// even when a pick from another month is left.
const selectionOf = ({
  selectedIds,
  onChange,
}: Pick<PickerProps, 'selectedIds' | 'onChange'>) => {
  const isAll = selectedIds.length === 0;
  const isPicked = (id: string) => selectedIds.includes(id);
  const toggle = (id: string) => {
    if (isAll) return onChange([id]);
    if (!selectedIds.includes(id)) return onChange([...selectedIds, id]);
    onChange(selectedIds.filter((s) => s !== id));
  };
  return { isAll, isPicked, toggle, selectAll: () => onChange([]) };
};

const ROW_CLASS =
  'flex min-h-11 w-full cursor-pointer items-center gap-2.5 rounded-lg px-3 py-1.5 text-left transition-colors hover:bg-gray-100';

const InstructorAvatar = ({
  item,
  size,
}: {
  item: Instructor;
  size: 'xs' | 'sm';
}) => (
  <MBAvatar
    size={size}
    hasBorder
    name={getFullName(item.first_name, item.last_name)}
    src={item.profile_picture ?? undefined}
  />
);

// The checkboxes toggle on Space only, as checkboxes do; a filter list is
// also expected to answer Enter, which reaches the row from the checkbox.
const onEnter = (action: () => void) => (event: KeyboardEvent) => {
  if (event.key !== 'Enter') return;
  event.preventDefault();
  action();
};

const InstructorList = (props: PickerProps) => {
  const { instructors } = props;
  const { isAll, isPicked, toggle, selectAll } = selectionOf(props);

  return (
    <div
      role="group"
      aria-label="Instructors"
      className="flex flex-col gap-0.5"
    >
      {/* The checkbox is the focusable control; the row widens its target. */}
      <div
        className={ROW_CLASS}
        onClick={selectAll}
        onKeyDown={onEnter(selectAll)}
      >
        <span className="flex-1 text-sm font-bold text-gray-900">
          All instructors
        </span>
        <MBCheckbox checked={isAll} aria-label="All instructors" />
      </div>
      {instructors.map((item) => (
        <div
          key={item.admin_user_id}
          className={ROW_CLASS}
          onClick={() => toggle(item.admin_user_id)}
          onKeyDown={onEnter(() => toggle(item.admin_user_id))}
        >
          <InstructorAvatar item={item} size="sm" />
          <span className="flex-1 text-sm font-medium text-gray-900">
            {nameOf(item)}
          </span>
          <MBCheckbox
            checked={isPicked(item.admin_user_id)}
            aria-label={nameOf(item)}
          />
        </div>
      ))}
    </div>
  );
};

const TRIGGER_SIZE = {
  md: {
    button: 'gap-2.5 p-2 pr-3.5 text-sm',
    avatar: 'xs',
    label: undefined,
    icon: 'size-4',
  },
  lg: {
    button: 'w-full gap-3 p-2 pr-4 text-base',
    avatar: 'sm',
    label: 'flex-1 text-left',
    icon: 'size-5',
  },
} as const;

const PickerTrigger = forwardRef<
  HTMLButtonElement,
  {
    instructors: Instructor[];
    selectedIds: string[];
    size?: keyof typeof TRIGGER_SIZE;
  } & Omit<ComponentPropsWithoutRef<'button'>, 'onChange'>
>(({ instructors, selectedIds, size = 'md', ...buttonProps }, ref) => {
  const sizes = TRIGGER_SIZE[size];
  const isAll = selectedIds.length === 0;
  const shown = isAll
    ? instructors
    : instructors.filter((i) => selectedIds.includes(i.admin_user_id));
  const label = isAll
    ? 'All instructors'
    : shown.length === 1
      ? nameOf(shown[0])
      : `${shown.length} instructors`;

  return (
    <button
      ref={ref}
      type="button"
      aria-label={`Instructors: ${label}`}
      {...buttonProps}
      className={mergeClassNames(
        'mb-focus-visible inline-flex shrink-0 cursor-pointer items-center rounded-xl bg-white font-semibold text-gray-900 shadow-skeu-secondary outline-none transition-all duration-200 hover:bg-gray-200 active:translate-y-px active:scale-[0.975]',
        sizes.button
      )}
    >
      <span aria-hidden className="flex">
        <AvatarGroup max={2} size={sizes.avatar}>
          {shown.map((item) => (
            <InstructorAvatar
              key={item.admin_user_id}
              item={item}
              size={sizes.avatar}
            />
          ))}
        </AvatarGroup>
      </span>
      <span className={sizes.label}>{label}</span>
      <ChevronDown className={mergeClassNames('text-gray-500', sizes.icon)} />
    </button>
  );
});
PickerTrigger.displayName = 'PickerTrigger';

// Five or more instructors no longer fit as pills: one button opens the list,
// as a bottom sheet on a phone and a popover from sm.
export const InstructorPicker = (props: PickerProps) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const { isAll, selectAll } = selectionOf(props);

  if (isMobile) {
    return (
      <div className="flex">
        <Modal.Root open={open} onOpenChange={setOpen}>
          <Modal.Trigger asChild>
            <PickerTrigger
              size="lg"
              instructors={props.instructors}
              selectedIds={props.selectedIds}
            />
          </Modal.Trigger>
          <Modal.Content size="sm" aria-describedby={undefined}>
            <Modal.Header>
              <Modal.Title>Show class times with</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-2">
              <InstructorList {...props} />
            </Modal.Body>
            <Modal.Footer
              layout={isAll ? 'stacked' : 'equal-row'}
              className="pb-[max(2.5rem,env(safe-area-inset-bottom))]"
            >
              {!isAll && (
                <Button variant="secondary-gray" onClick={selectAll}>
                  Reset filters
                </Button>
              )}
              <Button variant="primary" onClick={() => setOpen(false)}>
                Show times
              </Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Root>
      </div>
    );
  }

  return (
    <div className="flex">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <PickerTrigger
            instructors={props.instructors}
            selectedIds={props.selectedIds}
          />
        </Popover.Trigger>
        <Popover.Content
          aria-label="Show class times with"
          side="bottom"
          align="end"
          sideOffset={8}
          className="w-72 max-w-none overflow-hidden p-0"
        >
          {/* Scrolls inside the rounded panel so the corners clip the bar. */}
          <div className="max-h-80 overflow-y-auto p-1.5">
            <InstructorList {...props} />
          </div>
        </Popover.Content>
      </Popover.Root>
    </div>
  );
};
